import { CardType } from "../../../types";
import { isCardPresent } from "../../../utils";
import {
  QuickRankRenderableDestinations,
  QuickRankNonRenderableDestinations,
  QuickRankState,
  QuickRankActionHandler,
  QuickRankGameResults,
} from "../quickrank";

const nextRound = (state: QuickRankState) => {
  const { DECK, HAND, RATED } = QuickRankRenderableDestinations;
  const { ALL, TO_RATE } = QuickRankNonRenderableDestinations;
  const {
    configuration: { numberOfHands, cardsPerHand },
    status: { shouldChangeRound, isGameFinished, round },
    cards: { [ALL]: all, [DECK]: deck, [HAND]: hand, [RATED]: rated, [TO_RATE]: toRate },
  } = state;

  if (isGameFinished && toRate.length && !rated.length) {
    const newToRate = [...toRate];
    const newRated = [newToRate.shift()];
    return {
      ...state,
      cards: {
        ...state.cards,
        [RATED]: newRated,
        [TO_RATE]: newToRate,
      },
      status: {
        ...state.status,
        isRatingRejectedCards: true,
      },
    };
  }

  if (isGameFinished || !shouldChangeRound || rated.length) {
    return state;
  }

  const newRound = round + 1;
  const rejected = hand.filter((card) => card !== null);
  const newAll = all.map((card) => (!rejected.includes(card) ? card : null));
  const newDeck = [
    ...deck.slice(0, cardsPerHand * round),
    ...(new Array(cardsPerHand).fill(null) as CardType[]),
    ...deck.slice(cardsPerHand * round + cardsPerHand),
  ];
  const newHand = deck.slice(cardsPerHand * round, cardsPerHand * newRound);

  return {
    ...state,
    cards: {
      ...state.cards,
      [ALL]: newAll,
      [DECK]: newDeck,
      [HAND]: newHand,
    },
    status: {
      ...state.status,
      round: newRound,
      shouldChangeRound: false,
      isGameFinished: newRound > numberOfHands,
      isRatingRejectedCards: false,
    },
  };
};

const incrementResultCount = (results: QuickRankGameResults, selectedCard: CardType) => {
  const newResults = { ...results };
  const newAttributeResult = { ...newResults[selectedCard.attribute.id] };
  newAttributeResult.count += 1;
  newResults[selectedCard.attribute.id] = newAttributeResult;
  return newResults;
};

const selectCard = (state: QuickRankState, selectedCard: CardType) => {
  const { HAND, RATED } = QuickRankRenderableDestinations;
  const { ALL, TO_RATE } = QuickRankNonRenderableDestinations;
  const {
    cards: { [ALL]: all, [HAND]: hand, [TO_RATE]: toRate },
    status: { shouldChangeRound },
    configuration: { showRatingScale },
    results,
  } = state;

  if (shouldChangeRound) {
    return state;
  }

  const shouldBeRated = showRatingScale && isCardPresent(selectedCard, toRate);

  const newResults = incrementResultCount(results, selectedCard);
  const newHand = hand.map(() => null);
  const newAll = all.map((card) => {
    const isOnHand = hand.includes(card);
    const isSelected = card === selectedCard;

    if (!isOnHand) {
      return card;
    }

    if (shouldBeRated && isSelected) {
      return card;
    }

    return null;
  });

  const isNewAllEmpty = newAll.every((card) => card === null);

  const newRated = shouldBeRated ? [selectedCard] : [];

  return {
    ...state,
    cards: {
      ...state.cards,
      [ALL]: newAll,
      [HAND]: newHand,
      [RATED]: newRated,
    },
    results: newResults,
    status: {
      ...state.status,
      isRatingRejectedCards: isNewAllEmpty && showRatingScale,
      shouldChangeRound: !shouldBeRated,
    },
  };
};

const setResultRating = (results: QuickRankGameResults, card: CardType, rating: number) => {
  const newResults = { ...results };
  const newAttributeResult = { ...newResults[card.attribute.id] };
  newAttributeResult.rating = rating;
  newResults[card.attribute.id] = newAttributeResult;
  return newResults;
};

const rateCard = (state: QuickRankState, ratingScore: number) => {
  const { RATED } = QuickRankRenderableDestinations;
  const { ALL, TO_RATE } = QuickRankNonRenderableDestinations;
  const {
    rating,
    cards: {
      [ALL]: all,
      [RATED]: [ratedCard],
      [TO_RATE]: toRate,
    },
    results,
  } = state;

  if (!ratedCard) {
    return state;
  }

  const newResults = setResultRating(results, ratedCard, ratingScore);
  const newAll = all.map((card) => (card === ratedCard ? null : card));
  const isNewAllEmpty = newAll.every((card) => card === null);
  const newToRate = [...toRate].filter((card) => card?.attribute !== ratedCard.attribute);
  const newRated = [];

  if (isNewAllEmpty && newToRate.length) {
    const nextToRate = newToRate[0];
    nextToRate && newRated.push(nextToRate);
  }

  const newIsGameFinished = isNewAllEmpty && newToRate.length === 0 && newRated.length === 0;

  return {
    ...state,
    cards: {
      ...state.cards,
      [RATED]: newRated,
      [TO_RATE]: newToRate,
      [ALL]: newAll,
    },
    rating: [
      ...rating,
      {
        attribute: ratedCard.attribute,
        score: ratingScore,
      },
    ],
    status: {
      ...state.status,
      shouldChangeRound: true,
      isRatingRejectedCards: isNewAllEmpty,
      isGameFinished: newIsGameFinished,
    },
    results: newResults,
  };
};

export default {
  NEXT_ROUND: nextRound,
  SELECT_CARD: selectCard,
  RATE_CARD: rateCard,
} as QuickRankActionHandler;
